import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlayIcon, StopIcon, PlusIcon, GasPumpIcon, MapPinIcon, TruckIcon, CameraIcon } from "@phosphor-icons/react";
import { fmtDT, fmtTime } from "@/lib/tz";
import { sortByUnit } from "@/lib/unitSort";

const EVENT_KINDS = [
  { k: "trailer_hooked", label: "Trailer hooked" },
  { k: "trailer_switch", label: "Trailer switched" },
  { k: "border_crossing", label: "Border crossing" },
  { k: "fuel_up", label: "Fuel up ⛽" },
  { k: "break_30", label: "30-min break" },
  { k: "sleep", label: "Sleep" },
  { k: "pickup", label: "Pickup" },
  { k: "delivery", label: "Delivery" },
  { k: "reset_34", label: "34-hr reset" },
  { k: "note", label: "Note" },
  { k: "repair", label: "Repair" },  // per spec: repair last
];

const EVENT_LABEL_MAP = Object.fromEntries(EVENT_KINDS.map((e) => [e.k, e.label]));

function useGeolocation(active) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!active) return;
    if (!navigator.geolocation) return;
    const watch = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude, at: new Date().toISOString() }),
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watch);
  }, [active]);
  return pos;
}

export default function DriverTripCard({ trucks, trailers }) {
  const [active, setActive] = useState(null);      // active trip
  const [loading, setLoading] = useState(true);
  const [openStart, setOpenStart] = useState(false);
  const [openEvent, setOpenEvent] = useState(false);
  const [openEnd, setOpenEnd] = useState(false);
  const [openFuel, setOpenFuel] = useState(false);
  const [openOutside, setOpenOutside] = useState(false);
  const [openAttach, setOpenAttach] = useState(false);
  const [history, setHistory] = useState([]);

  const gps = useGeolocation(!!active);
  const lastPushRef = useRef(0);

  // Push GPS ping every ~2 min while trip is active
  useEffect(() => {
    if (!active || !gps) return;
    const now = Date.now();
    if (now - lastPushRef.current < 120000) return;
    lastPushRef.current = now;
    api.post(`/driver/trips/${active.id}/location`, { lat: gps.lat, lng: gps.lng, at: gps.at }).catch(() => {});
  }, [active, gps]);

  const loadAll = async () => {
    try {
      const [a, h] = await Promise.all([api.get("/driver/trips/active"), api.get("/driver/trips?limit=5")]);
      setActive(a.data && a.data.id ? a.data : null);
      setHistory(h.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  if (loading) return null;

  return (
    <div className="mb-8">
      {active ? (
        <ActiveTripCard
          trip={active}
          trucks={trucks}
          trailers={trailers}
          gps={gps}
          onAddEvent={() => setOpenEvent(true)}
          onAddFuel={() => setOpenFuel(true)}
          onEnd={() => setOpenEnd(true)}
          onAttach={() => setOpenAttach(true)}
        />
      ) : (
        <NoActiveTripCard onStart={() => setOpenStart(true)} historyCount={history.length} />
      )}

      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-2">Recent trips</div>
          <div className="border border-slate-200 rounded-sm divide-y divide-slate-200 bg-white">
            {history.slice(0, 3).map((t) => (
              <div key={t.id} className="p-2 text-xs font-mono flex items-center gap-3">
                <span className="font-bold">{t.trip_number}</span>
                <span className="text-slate-500">{(t.start_at || "").slice(0, 10)}</span>
                <span className="ml-auto">{Math.round(t.total_miles || 0).toLocaleString()} mi</span>
                <span className="text-slate-500">· {(t.events || []).length} events</span>
                <span className={`uppercase text-[10px] font-bold ${t.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <StartTripDialog
        open={openStart}
        onOpenChange={setOpenStart}
        trucks={trucks}
        trailers={trailers}
        gps={gps}
        onStarted={async () => { setOpenStart(false); await loadAll(); }}
        onNeedOutside={() => { setOpenStart(false); setOpenOutside(true); }}
      />
      <OutsideTrailerDialog
        open={openOutside}
        onOpenChange={setOpenOutside}
        trucks={trucks}
        gps={gps}
        onStarted={async () => { setOpenOutside(false); await loadAll(); }}
      />
      <AddEventDialog
        open={openEvent}
        onOpenChange={setOpenEvent}
        trip={active}
        gps={gps}
        onDone={async () => { setOpenEvent(false); await loadAll(); }}
      />
      <FuelUpDialog
        open={openFuel}
        onOpenChange={setOpenFuel}
        trip={active}
        trucks={trucks}
        trailers={trailers}
        gps={gps}
        onDone={async () => { setOpenFuel(false); await loadAll(); }}
      />
      <EndTripDialog
        open={openEnd}
        onOpenChange={setOpenEnd}
        trip={active}
        gps={gps}
        onEnded={async () => { setOpenEnd(false); await loadAll(); }}
      />
      <AttachDocDialog
        open={openAttach}
        onOpenChange={setOpenAttach}
        trip={active}
        onDone={async () => { setOpenAttach(false); await loadAll(); }}
      />
    </div>
  );
}

function AttachDocDialog({ open, onOpenChange, trip, onDone }) {
  const [kind, setKind] = useState("stamped_aci");
  const [photo, setPhoto] = useState("");
  const [notes, setNotes] = useState("");
  const fileRef = useRef(null);

  const pick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!trip || !photo) { toast.error("Take or pick a photo first"); return; }
    try {
      await api.post(`/driver/trips/${trip.id}/attachments`, {
        kind,
        photo,
        notes,
      });
      toast.success("Document attached");
      setKind("stamped_aci"); setPhoto(""); setNotes("");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="driver-attach-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Attach document</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">Document type *</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="attach-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stamped_aci">Stamped ACI</SelectItem>
                <SelectItem value="trip_envelope">Trip envelope</SelectItem>
                <SelectItem value="pod">POD (Proof of delivery)</SelectItem>
                <SelectItem value="bol">BOL (Bill of lading)</SelectItem>
                <SelectItem value="fuel_receipt">Fuel receipt</SelectItem>
                <SelectItem value="inspection">Inspection</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) pick(f); }} />
            <Button type="button" onClick={() => fileRef.current?.click()} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-sm" data-testid="attach-photo-btn">
              <CameraIcon size={16} weight="bold" className="mr-1" /> {photo ? "Replace photo" : "Snap / choose photo"}
            </Button>
            {photo && <img src={photo} alt="doc" className="w-full max-h-56 object-contain mt-2 border border-slate-300 rounded-sm" />}
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="attach-submit">Attach</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NoActiveTripCard({ onStart, historyCount }) {
  return (
    <div className="bg-blue-600 text-white rounded-sm p-6 flex flex-col md:flex-row items-center justify-between gap-4 shadow" data-testid="driver-no-trip">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-blue-200 font-bold">/// Trip</div>
        <div className="font-display text-2xl font-black tracking-tight mt-1">Ready to hit the road?</div>
        <div className="text-blue-100 text-sm mt-1">Start a trip now — every event, every mile, every fill-up gets logged.</div>
      </div>
      <button onClick={onStart} data-testid="driver-start-trip" className="bg-white text-blue-700 hover:bg-blue-50 px-6 py-3 rounded-sm font-black uppercase tracking-wider inline-flex items-center gap-2 shadow-sm">
        <PlayIcon size={18} weight="fill" /> Start a trip
      </button>
    </div>
  );
}

function ActiveTripCard({ trip, trucks, trailers, gps, onAddEvent, onAddFuel, onEnd, onAttach }) {
  const truck = trucks.find((t) => t.id === trip.truck_id);
  const trailer = trailers.find((t) => t.id === trip.trailer_id);
  return (
    <div className="bg-white border-2 border-blue-500 rounded-sm p-5" data-testid="driver-active-trip">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-blue-600 font-bold">/// Active trip</div>
          <div className="font-display text-2xl font-black tracking-tight mt-1">Trip {trip.trip_number}</div>
          <div className="text-xs font-mono text-slate-500 mt-1">
            Started {fmtDT(trip.start_at)}
            {truck && ` · #${truck.unit_number}`}
            {trailer && ` · trailer #${trailer.unit_number}`}
            {trip.outside_trailer_id && ` · outside trailer`}
          </div>
          {gps && (
            <div className="text-[11px] font-mono text-emerald-600 mt-1 flex items-center gap-1">
              <MapPinIcon size={11} weight="fill" /> GPS live · {gps.lat.toFixed(3)}, {gps.lng.toFixed(3)}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onAddFuel} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="driver-add-fuel">
            <GasPumpIcon size={16} weight="bold" className="mr-1" /> Fuel up
          </Button>
          <Button onClick={onAttach} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="driver-add-doc">
            <CameraIcon size={16} weight="bold" className="mr-1" /> Attach doc
          </Button>
          <Button onClick={onAddEvent} className="bg-slate-800 hover:bg-slate-900 text-white rounded-sm font-bold" data-testid="driver-add-event">
            <PlusIcon size={16} weight="bold" className="mr-1" /> Add event
          </Button>
          <Button onClick={onEnd} className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold" data-testid="driver-end-trip">
            <StopIcon size={16} weight="fill" className="mr-1" /> End trip
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm divide-y divide-slate-200 max-h-64 overflow-y-auto">
        {(trip.events || []).length === 0 ? (
          <div className="p-4 text-center text-xs text-slate-500">No events yet. Log your first — trailer hooked, pickup, fuel, etc.</div>
        ) : (
          (trip.events || []).slice().reverse().map((ev, i) => (
            <div key={i} className="p-2.5 text-xs flex items-center gap-2">
              <span className="font-mono text-slate-500 w-32 flex-shrink-0">{fmtTime(ev.at)} · {(ev.at || "").slice(0, 10)}</span>
              <span className="font-bold text-slate-800 w-36 flex-shrink-0">{EVENT_LABEL_MAP[ev.kind] || ev.kind}</span>
              <span className="text-slate-600 truncate">{ev.location?.address || ev.notes || ""}</span>
              {ev.kind === "fuel_up" && <span className="font-mono font-bold text-emerald-700 ml-auto">${Number(ev.details?.amount || 0).toFixed(2)}</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StartTripDialog({ open, onOpenChange, trucks, trailers, gps, onStarted, onNeedOutside }) {
  const [form, setForm] = useState({ trip_number: "", truck_id: "", trailer_id: "", start_odometer: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        trip_number: form.trip_number,
        truck_id: form.truck_id,
        trailer_id: form.trailer_id || null,
        start_odometer: Number(form.start_odometer) || 0,
        notes: form.notes,
      };
      if (gps) body.start_location = { lat: gps.lat, lng: gps.lng };
      await api.post("/driver/trips/start", body);
      toast.success("Trip started — safe travels");
      onStarted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="driver-start-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Start a trip</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider font-bold">Trip # *</Label>
            <Input required value={form.trip_number} onChange={(e) => setForm({ ...form, trip_number: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="e.g. T-2001" data-testid="start-trip-number" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Truck *</Label>
            <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="start-truck"><SelectValue placeholder="Pick truck" /></SelectTrigger>
              <SelectContent>{sortByUnit(trucks).map((t) => (<SelectItem key={t.id} value={t.id}>#{t.unit_number}{t.make ? ` · ${t.make}` : ""}</SelectItem>))}</SelectContent>
            </Select></div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Trailer</Label>
            <Select value={form.trailer_id} onValueChange={(v) => setForm({ ...form, trailer_id: v })}>
              <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Pick fleet trailer (optional)" /></SelectTrigger>
              <SelectContent>{sortByUnit(trailers).map((t) => (<SelectItem key={t.id} value={t.id}>#{t.unit_number}</SelectItem>))}</SelectContent>
            </Select>
            <button type="button" onClick={onNeedOutside} className="text-xs text-blue-600 hover:underline font-semibold mt-1" data-testid="start-outside-trailer">
              → Outside carrier trailer? Add it with photos instead
            </button>
          </div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Start odometer *</Label>
            <Input required type="number" step="1" value={form.start_odometer} onChange={(e) => setForm({ ...form, start_odometer: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="100000" data-testid="start-odometer" /></div>
          <div><Label className="text-xs uppercase tracking-wider font-bold">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm" rows={2} /></div>
          {!gps && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">Location permission blocked. Please enable location in your browser so we can track the route.</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="start-trip-submit">
              <PlayIcon size={16} weight="fill" className="mr-1" /> Start trip
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OutsideTrailerDialog({ open, onOpenChange, trucks, gps, onStarted }) {
  const [form, setForm] = useState({ trip_number: "", truck_id: "", start_odometer: "", trailer_number: "", plate_number: "", carrier: "", damage_notes: "", photos: [] });
  const fileRef = useRef(null);

  const addPhoto = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photos: [...f.photos, reader.result] }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        trip_number: form.trip_number,
        truck_id: form.truck_id,
        start_odometer: Number(form.start_odometer) || 0,
        outside_trailer: {
          trailer_number: form.trailer_number,
          plate_number: form.plate_number,
          carrier: form.carrier,
          damage_notes: form.damage_notes,
          photos: form.photos,
        },
      };
      if (gps) body.start_location = { lat: gps.lat, lng: gps.lng };
      await api.post("/driver/trips/start", body);
      toast.success("Trip started with outside trailer");
      onStarted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="driver-outside-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Start trip · outside carrier trailer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs uppercase font-bold">Trip # *</Label>
              <Input required value={form.trip_number} onChange={(e) => setForm({ ...form, trip_number: e.target.value })} className="mt-1 rounded-sm font-mono" /></div>
            <div><Label className="text-xs uppercase font-bold">Start odo *</Label>
              <Input required type="number" value={form.start_odometer} onChange={(e) => setForm({ ...form, start_odometer: e.target.value })} className="mt-1 rounded-sm font-mono" /></div>
          </div>
          <div><Label className="text-xs uppercase font-bold">Truck *</Label>
            <Select value={form.truck_id} onValueChange={(v) => setForm({ ...form, truck_id: v })}>
              <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Pick truck" /></SelectTrigger>
              <SelectContent>{sortByUnit(trucks).map((t) => (<SelectItem key={t.id} value={t.id}>#{t.unit_number}</SelectItem>))}</SelectContent>
            </Select></div>
          <div className="border-t border-slate-200 pt-3">
            <div className="text-[10px] uppercase font-mono text-slate-500 font-bold mb-2">Outside trailer details</div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase font-bold">Trailer # *</Label>
                <Input required value={form.trailer_number} onChange={(e) => setForm({ ...form, trailer_number: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="outside-trailer-number" /></div>
              <div><Label className="text-xs uppercase font-bold">Plate #</Label>
                <Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} className="mt-1 rounded-sm font-mono" data-testid="outside-plate" /></div>
            </div>
            <div><Label className="text-xs uppercase font-bold">Carrier</Label>
              <Input value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label className="text-xs uppercase font-bold">Damage notes (if any)</Label>
              <Textarea value={form.damage_notes} onChange={(e) => setForm({ ...form, damage_notes: e.target.value })} className="mt-1 rounded-sm" rows={2} /></div>
            <div>
              <Label className="text-xs uppercase font-bold">Photos ({form.photos.length})</Label>
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) addPhoto(f); }} />
              <Button type="button" onClick={() => fileRef.current?.click()} className="mt-1 w-full bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-sm" data-testid="outside-photo-btn">
                <CameraIcon size={16} weight="bold" className="mr-1" /> Take a photo
              </Button>
              {form.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.photos.map((p, i) => (
                    <div key={i} className="relative">
                      <img src={p} alt={`photo ${i + 1}`} className="w-14 h-14 object-cover rounded-sm border border-slate-300" />
                      <button type="button" onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 rounded-full text-[10px] font-bold">×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="outside-start-submit">Start trip</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddEventDialog({ open, onOpenChange, trip, gps, onDone }) {
  const [kind, setKind] = useState("trailer_hooked");
  const [notes, setNotes] = useState("");
  const [when, setWhen] = useState("");  // "" = now, else ISO from datetime-local
  const submit = async (e) => {
    e.preventDefault();
    if (!trip) return;
    try {
      const body = { kind, notes };
      if (gps) body.location = { lat: gps.lat, lng: gps.lng };
      if (when) body.at = new Date(when).toISOString();
      await api.post(`/driver/trips/${trip.id}/events`, body);
      toast.success("Event logged");
      setKind("trailer_hooked"); setNotes(""); setWhen("");
      onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.code === "gps_gap") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Failed");
      }
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="driver-event-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Add event</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs uppercase font-bold">Event *</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="event-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{EVENT_KINDS.filter((e) => e.k !== "fuel_up").map((e) => (<SelectItem key={e.k} value={e.k}>{e.label}</SelectItem>))}</SelectContent>
            </Select></div>
          <div><Label className="text-xs uppercase font-bold">When</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1 rounded-sm font-mono" />
            <div className="text-[11px] text-slate-500 mt-1">Leave blank if this happened just now. Backfilled events are checked against your GPS trail.</div>
          </div>
          <div><Label className="text-xs uppercase font-bold">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold" data-testid="event-submit">Log event</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FuelUpDialog({ open, onOpenChange, trip, trucks, trailers, gps, onDone }) {
  const [form, setForm] = useState({ unit_type: "truck", unit_id: "", amount: "", gallons: "", vendor: "", price_per_gallon: "", receipt_photo: "" });
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && trip) {
      setForm((f) => ({ ...f, unit_id: trip.truck_id || "" }));
    }
  }, [open, trip]);

  const addReceipt = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, receipt_photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!trip) return;
    if (!form.unit_id) { toast.error("Pick a truck or trailer"); return; }
    if (!form.amount) { toast.error("Enter amount fueled"); return; }
    try {
      const body = {
        kind: "fuel_up",
        location: gps ? { lat: gps.lat, lng: gps.lng, address: form.vendor } : undefined,
        details: {
          amount: Number(form.amount),
          gallons: Number(form.gallons) || 0,
          price_per_gallon: Number(form.price_per_gallon) || 0,
          unit_type: form.unit_type,
          unit_id: form.unit_id,
          vendor: form.vendor,
          receipt_photo: form.receipt_photo,
        },
      };
      await api.post(`/driver/trips/${trip.id}/events`, body);
      toast.success("Fuel logged");
      setForm({ unit_type: "truck", unit_id: trip.truck_id || "", amount: "", gallons: "", vendor: "", price_per_gallon: "", receipt_photo: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const options = form.unit_type === "truck" ? trucks : trailers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="driver-fuel-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Fuel up ⛽</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">Fueling *</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, unit_type: "truck", unit_id: trip?.truck_id || "" })} data-testid="fuel-truck-btn"
                className={`px-3 py-2 border-2 rounded-sm text-sm font-bold ${form.unit_type === "truck" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}><TruckIcon size={14} weight="bold" className="inline mr-1" /> Truck</button>
              <button type="button" onClick={() => setForm({ ...form, unit_type: "trailer", unit_id: trip?.trailer_id || "" })} data-testid="fuel-trailer-btn"
                className={`px-3 py-2 border-2 rounded-sm text-sm font-bold ${form.unit_type === "trailer" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 hover:bg-slate-50"}`}>Reefer / Trailer</button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">{form.unit_type === "truck" ? "Truck" : "Trailer/Reefer"} *</Label>
            <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="fuel-unit"><SelectValue placeholder="Pick unit" /></SelectTrigger>
              <SelectContent>
                {options.map((u) => (<SelectItem key={u.id} value={u.id}>#{u.unit_number}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs uppercase font-bold">Amount $ *</Label>
              <Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="250.00" data-testid="fuel-amount" /></div>
            <div><Label className="text-xs uppercase font-bold">Gallons</Label>
              <Input type="number" step="0.1" value={form.gallons} onChange={(e) => setForm({ ...form, gallons: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="60" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs uppercase font-bold">Vendor</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-1 rounded-sm" placeholder="Pilot, Loves, TA" /></div>
            <div><Label className="text-xs uppercase font-bold">$/gal</Label>
              <Input type="number" step="0.001" value={form.price_per_gallon} onChange={(e) => setForm({ ...form, price_per_gallon: e.target.value })} className="mt-1 rounded-sm font-mono" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">Receipt photo</Label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) addReceipt(f); }} />
            <Button type="button" onClick={() => fileRef.current?.click()} className="mt-1 w-full bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-sm" data-testid="fuel-receipt-btn">
              <CameraIcon size={16} weight="bold" className="mr-1" /> {form.receipt_photo ? "Replace receipt" : "Snap receipt"}
            </Button>
            {form.receipt_photo && <img src={form.receipt_photo} alt="receipt" className="w-full max-h-40 object-contain mt-2 border border-slate-300 rounded-sm" />}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="fuel-submit">Log fuel</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EndTripDialog({ open, onOpenChange, trip, gps, onEnded }) {
  const [odo, setOdo] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!trip) return;
    const n = Number(odo);
    if (!n || n <= Number(trip.start_odometer || 0)) {
      toast.error("End odometer must be greater than start");
      return;
    }
    try {
      const body = { end_odometer: n, notes };
      if (gps) body.end_location = { lat: gps.lat, lng: gps.lng };
      const { data } = await api.post(`/driver/trips/${trip.id}/end`, body);
      toast.success(`Trip completed — ${Math.round(data.total_miles || 0)} miles`);
      setOdo(""); setNotes("");
      onEnded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="driver-end-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">End trip</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-slate-500 font-mono">Start odo: {Math.round(trip?.start_odometer || 0).toLocaleString()}</div>
          <div><Label className="text-xs uppercase font-bold">End odometer *</Label>
            <Input required type="number" step="1" value={odo} onChange={(e) => setOdo(e.target.value)} className="mt-1 rounded-sm font-mono" placeholder="100500" data-testid="end-odometer" /></div>
          <div><Label className="text-xs uppercase font-bold">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold" data-testid="end-trip-submit">End trip</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
